import { timesheetCollectionNew } from '../../../services/firebase';
import { addWeeks, eachDayOfInterval, endOfWeek, format, startOfWeek, subWeeks } from 'date-fns';
import { convertMinutesToRoundedDecimalHours } from '../../../utils/utils';

export const getDeliverablesList = (project) => {
  return (
    project?.deliverables?.map((x) => ({
      label: x?.name,
      value: x?.name,
    })) || []
  );
};

export const getEnabledRolesListForDeliverable = (deliverable, resources, plannerType) => {
  // --- 1) fixed-fee projects/deliverables: all top-level roles ---
  if (plannerType === 'Fixed Fee Project' || plannerType === 'Fixed Fee Deliverable') {
    const allRoles = resources.map((r) => r.role);
    const unique = [...new Set(allRoles)];
    return unique.map((role) => ({ label: role, value: role }));
  }

  // --- 2) time & materials: only roles enabled on the tasks themselves ---
  const arrayOfRoles = [];

  deliverable?.tasks?.map((task) => {
    const taskResources = task?.resources;
    taskResources.map((resource) => {
      const isResourceEnabled = resource?.isEnabled;
      if (!isResourceEnabled) return '';
      const foundResource = resources?.find((res) => res?.id === resource?.id);

      arrayOfRoles.push(foundResource?.role);
      return '';
    });
    return '';
  });

  const uniqueRoles = [...new Set(arrayOfRoles)];

  return uniqueRoles?.map((x) => ({ label: `${x}`, value: x })) || [];
};

export const toggleStarredProject = async (user, projectId, isStarred) => {
  const userName = getUserNameForFirestore(user);
  const userDocRef = timesheetCollectionNew.doc(userName);

  try {
    const docSnapshot = await userDocRef.get();
    const starredProjects = docSnapshot.data()?.starredProjects || [];

    let updatedStarredProjects = [...starredProjects];
    if (isStarred) {
      updatedStarredProjects = updatedStarredProjects.filter((id) => id !== projectId);
    } else {
      updatedStarredProjects.push(projectId);
    }

    console.log('Updating starred projects:', starredProjects);
    await userDocRef.set({ starredProjects: updatedStarredProjects }, { merge: true });
    console.log('Starred projects updated successfully!');
  } catch (error) {
    console.error('Error updating starred projects:', error);
  }
};

export const getRolesList = (project) => {
  // Use the project's top-level resources array to derive roles.
  const roles = project?.resources?.map((resource) => resource?.role) || [];
  const uniqueRoles = [...new Set(roles)];
  return uniqueRoles.map((role) => ({ label: role, value: role }));
};

export const getUserNameForFirestore = (user) => {
  if (user && user['firstName'] && user['surname']) {
    const fullName = `${user['firstName']}_${user['surname']}`;
    return fullName.replace(/\s+/g, '_').toLowerCase();
  } else if (user && user.displayName) {
    return user.displayName.replace(/\s+/g, '_').toLowerCase();
  }
  return 'default_username';
};

// Helper functions
export const getCurrentWeek = () => {
  const start = startOfWeek(new Date(), { weekStartsOn: 1 });
  const end = endOfWeek(new Date(), { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end });
};

export const getNextWeek = (date) => {
  const start = addWeeks(startOfWeek(date, { weekStartsOn: 1 }), 1);
  const end = endOfWeek(start, { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end });
};

export const getPreviousWeek = (date) => {
  const start = subWeeks(startOfWeek(date, { weekStartsOn: 1 }), 1);
  const end = endOfWeek(start, { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end });
};

export const initializeWeeklyHours = (week = getCurrentWeek()) => {
  return week.map((day) => ({
    date: format(day, 'dd-MM-yyyy'),
    minutes: 0,
  }));
};

export const getInitialTableData = (currentWeek) => {
  return {
    id: 0,
    projectName: '',
    flowId: '',
    clientName: '',
    deliverable: '',
    role: '',
    isSearchRow: true,
    weeklyHours: initializeWeeklyHours(currentWeek),
  };
};

export const fetchWeeklyHoursMap = async (user, currentWeek, projects) => {
  const userName = getUserNameForFirestore(user);
  const weekHoursRef = timesheetCollectionNew
    .doc(userName)
    .collection('weeks')
    .doc(format(currentWeek[0], 'yyyy-MM-dd'));

  const starredProjectRef = timesheetCollectionNew.doc(userName);

  try {
    const weekHoursSnapshot = await weekHoursRef.get();

    let weeklyHoursData = undefined;

    if (weekHoursSnapshot.exists) {
      weeklyHoursData = weekHoursSnapshot.data();
    }

    const starredProjectsSnapshot = await starredProjectRef.get();
    let starredProjects = [];

    if (starredProjectsSnapshot.exists) {
      const data = starredProjectsSnapshot.data();
      starredProjects = data.starredProjects || [];
    }

    const processedData =
      weeklyHoursData?.entries?.map((project) => ({
        ...project,
        id: project.flowId,
        uniqueId: project.uniqueId,
        projectName: project.projectName,
        client: project.clientName,
        deliverable: project.deliverable,
        role: project.role,
        isSearchRow: false,
        weeklyHours: project.hours.map((hour) => ({
          date: hour.date,
          minutes: parseFloat(hour.hours) * 60,
          note: hour?.note || '',
        })),
        deliverablesList: getDeliverablesList(projects?.find((p) => p?.id === project.flowId)),
        rolesList: getRolesList(projects?.find((p) => p?.id === project.flowId)),
      })) || [];

    if (processedData.length === 0) {
      starredProjects.forEach((projectId) => {
        const selection = projects.find(
          (p) => p.id === projectId && !['Complete', 'On Hold', 'Cancelled'].includes(p.state),
        );
        if (selection) {
          const uniqueEntryId = `${selection.id}-${Date.now()}`;
          processedData.unshift({
            ...selection,
            uniqueId: uniqueEntryId,
            projectName: selection.projectName,
            deliverable: '',
            role: '',
            weeklyHours: initializeWeeklyHours(currentWeek),
            deliverablesList: getDeliverablesList(selection),
            rolesList: getRolesList(selection),
          });
        }
      });
    }

    return { weeklyHours: processedData, starredProjects };
  } catch (err) {
    console.error('Error fetching weekly hours and starred projects:', err);
    return { weeklyHours: [], starredProjects: [] };
  }
};

export const saveWeeklyHoursMutation = async ({
  user,
  currentWeek,
  tableData,
  projects: allProjects,
}) => {
  const userName = getUserNameForFirestore(user);

  const userDocRef = timesheetCollectionNew.doc(userName);
  const userDocsWeeksRef = timesheetCollectionNew.doc(userName).collection('weeks');

  try {
    console.log('Starting.....', new Date(Date.now()).toISOString());

    const weekStartingKey = format(currentWeek[0], 'yyyy-MM-dd');
    const weekDocRef = await userDocsWeeksRef.doc(weekStartingKey);
    const weekDocSnaphot = await weekDocRef.get();

    const weeklyHoursDataNew = await Promise.all(
      tableData
        .filter((item) => !item.isSearchRow)
        .map(async (item) => {
          let projectGroupName = '';
          if (item.addToExistingProject) {
            const linkedProject = allProjects.find(
              (project) => project.id.toString() === item.addToExistingProject.toString(),
            );
            if (linkedProject) {
              projectGroupName = linkedProject.projectName;
            }
          }

          return {
            weekStarting: weekStartingKey,
            projectName: item.projectName,
            projectGroupName: projectGroupName || '',
            flowId: item.id,
            uniqueId: item.uniqueId,
            clientName: item.client,
            deliverable: item.deliverable,
            deliverableId: item?.deliverableId || '',
            role: item.role,
            hours: item.weeklyHours.map((hour) => ({
              date: hour.date,
              hours: convertMinutesToRoundedDecimalHours(hour.minutes),
              note: hour?.note ? hour?.note : '',
              isNonBillable: item.isNonBillable || false,
              state: item.state || '',
            })),
          };
        }),
    );

    if (!weekDocSnaphot.exists) {
      console.log('Creating new document for user in timesheetNew:', userName);

      const userDocSnapshot = await userDocRef.get();
      if (!userDocSnapshot.exists) {
        await userDocRef.set(
          {
            starredProjects: [],
          },
          { merge: true },
        );
      }

      await weekDocRef.set(
        {
          entries: weeklyHoursDataNew,
          userId: userName,
          startingWeek: weekStartingKey,
          migratedAt: '',
        },
        { merge: true },
      );
    }

    if (weekDocSnaphot.exists) {
      console.log('Updating existing document for user in timesheetNew:', userName);
      await weekDocRef.update({
        entries: weeklyHoursDataNew,
      });
    }

    console.log('Weekly hours saved!!', new Date(Date.now()).toISOString());
  } catch (error) {
    console.error('Error saving or updating weekly hours:', error);
  }
};
